/**
 * Enhanced Error Boundary Component
 * Multi-Tenant Error Handling with User-Friendly UI
 *
 * @description Issue #283 対応 - エラー境界コンポーネント
 * @backend NestJS ErrorResponse統合
 * @features ユーザーフレンドリー・エラー分類・自動復旧
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  EnhancedApiError,
  createErrorInfo,
  logError,
  shouldLogoutOnError,
  shouldDisplayToUser,
  ErrorSeverity,
  type ErrorFallbackProps,
} from '../utils/error';
import logger from '../utils/logger';
import { clearRipiaStorage } from '../services/auth/shared/tokenStorage';

// ====================================
// Error Boundary State Interface
// ====================================

interface ErrorBoundaryState {
  hasError: boolean;
  error: EnhancedApiError | null;
  errorId: string | null;
  retryCount: number;
  componentStack?: string;
}

// ====================================
// Error Boundary Props Interface
// ====================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: EnhancedApiError, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  isolate?: boolean; // Whether to isolate errors to this boundary
  maxRetries?: number;
  enableAutoRetry?: boolean;
  className?: string;
}

// ====================================
// Enhanced Error Boundary Component
// ====================================

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorId: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const { enhancedError } = createErrorInfo(error);
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      error: enhancedError,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { enhancedError, componentStack } = createErrorInfo(error, errorInfo);

    // Update state with component stack
    this.setState({ componentStack });

    // Log the error
    logError(enhancedError, {
      componentStack,
      props: this.props,
      retryCount: this.state.retryCount,
    });

    // Call custom error handler
    this.props.onError?.(enhancedError, errorInfo);

    // Check if automatic logout is required
    if (shouldLogoutOnError(enhancedError)) {
      this.handleSecurityLogout(enhancedError);
    }

    // Attempt auto-retry for retryable errors
    if (this.props.enableAutoRetry && enhancedError.isRetryable) {
      this.scheduleAutoRetry(enhancedError);
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private handleSecurityLogout = (error: EnhancedApiError) => {
    logger.warn('[ErrorBoundary] Security error detected, triggering logout', {
      errorType: error.errorType,
      severity: error.severity,
    });

    // Clear Ripia-related storage and redirect to login
    try {
      clearRipiaStorage();
    } catch (clearError) {
      logger.error(
        '[ErrorBoundary] Failed to clear storage during security logout',
        clearError
      );
    }
    try {
      window.location.href = '/login?reason=session_expired';
    } catch (redirectError) {
      logger.error(
        '[ErrorBoundary] Failed to redirect during security logout',
        redirectError
      );
    }
  };

  private scheduleAutoRetry = (_error: EnhancedApiError) => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount >= maxRetries) {
      logger.log('[ErrorBoundary] Max retries reached, stopping auto-retry');
      return;
    }

    const delay = Math.min(5000, 1000 * Math.pow(2, retryCount)); // Max 5 seconds
    logger.log(
      `[ErrorBoundary] Scheduling auto-retry in ${delay}ms (attempt ${retryCount + 1})`
    );

    this.retryTimeoutId = setTimeout(() => {
      this.handleRetry();
    }, delay);
  };

  private handleRetry = () => {
    const { retryCount } = this.state;

    logger.log(`[ErrorBoundary] Attempting retry ${retryCount + 1}`);

    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorId: null,
      retryCount: prevState.retryCount + 1,
      componentStack: undefined,
    }));

    this.props.onReset?.();
  };

  private handleReset = () => {
    logger.log('[ErrorBoundary] Manual reset triggered');

    this.setState({
      hasError: false,
      error: null,
      errorId: null,
      retryCount: 0,
      componentStack: undefined,
    });

    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.props.onReset?.();
  };

  private renderErrorFallback = (): ReactNode => {
    const { fallback: FallbackComponent } = this.props;
    const { error } = this.state;

    if (!error) return null;

    // Use custom fallback component if provided
    if (FallbackComponent) {
      return (
        <FallbackComponent
          error={error}
          resetError={this.handleReset}
          retry={error.isRetryable ? this.handleRetry : undefined}
        />
      );
    }

    // Default fallback UI
    return (
      <DefaultErrorFallback
        error={error}
        resetError={this.handleReset}
        retry={error.isRetryable ? this.handleRetry : undefined}
      />
    );
  };

  render() {
    const { hasError } = this.state;
    const { children, className } = this.props;

    if (hasError) {
      return (
        <div className={`error-boundary ${className || ''}`}>
          {this.renderErrorFallback()}
        </div>
      );
    }

    return children;
  }
}

// ====================================
// Default Error Fallback Component
// ====================================

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  retry,
}) => {
  const shouldShow = shouldDisplayToUser(error);

  if (!shouldShow) {
    return (
      <div className="error-fallback error-fallback--minimal">
        <div className="error-content">
          <h3>問題が発生しました</h3>
          <p>
            一時的な問題が発生しました。しばらくしてから再度お試しください。
          </p>
          <div className="error-actions">
            <button onClick={resetError} className="btn btn-primary">
              再読み込み
            </button>
          </div>
        </div>
      </div>
    );
  }

  const suggestions = error.getSuggestions();

  return (
    <div className={`error-fallback error-fallback--${error.severity}`}>
      <div className="error-content">
        <div className="error-header">
          <div className="error-icon">
            {error.severity === ErrorSeverity.CRITICAL && '🚨'}
            {error.severity === ErrorSeverity.HIGH && '⚠️'}
            {error.severity === ErrorSeverity.MEDIUM && '❗'}
            {error.severity === ErrorSeverity.LOW && 'ℹ️'}
          </div>
          <h3 className="error-title">
            {error.severity === ErrorSeverity.CRITICAL && 'システムエラー'}
            {error.severity === ErrorSeverity.HIGH && 'エラーが発生しました'}
            {error.severity === ErrorSeverity.MEDIUM && 'エラーが発生しました'}
            {error.severity === ErrorSeverity.LOW && 'お知らせ'}
          </h3>
        </div>

        <div className="error-message">
          <p>{error.userMessage}</p>
        </div>

        {suggestions.length > 0 && (
          <div className="error-suggestions">
            <h4>解決方法:</h4>
            <ul>
              {suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="error-actions">
          {retry && (
            <button onClick={retry} className="btn btn-primary">
              再試行
            </button>
          )}
          <button onClick={resetError} className="btn btn-secondary">
            リセット
          </button>
        </div>

        {/* Technical details for development */}
        {import.meta.env?.MODE === 'development' && (
          <details className="error-technical">
            <summary>技術的詳細 (開発環境のみ)</summary>
            <div className="error-details">
              <p>
                <strong>Error Type:</strong> {error.errorType}
              </p>
              <p>
                <strong>Status Code:</strong> {error.statusCode}
              </p>
              <p>
                <strong>Timestamp:</strong> {error.timestamp}
              </p>
              <p>
                <strong>Request ID:</strong> {error.requestId || 'N/A'}
              </p>
              <p>
                <strong>Tenant ID:</strong> {error.tenantId || 'N/A'}
              </p>
              <p>
                <strong>Correlation ID:</strong> {error.correlationId || 'N/A'}
              </p>
              <p>
                <strong>Retryable:</strong> {error.isRetryable ? 'Yes' : 'No'}
              </p>
              {!!error.details && (
                <div>
                  <strong>Details:</strong>
                  <pre>{JSON.stringify(error.details, null, 2)}</pre>
                </div>
              )}
            </div>
          </details>
        )}
      </div>

      {/* CSS Styles - converted from styled-jsx to avoid TypeScript errors */}
      <style>{`
        .error-fallback {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          padding: 2rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }

        .error-fallback--critical {
          background-color: #f8d7da;
          border-color: #f5c6cb;
        }

        .error-fallback--high {
          background-color: #fff3cd;
          border-color: #ffeaa7;
        }

        .error-fallback--medium {
          background-color: #d1ecf1;
          border-color: #bee5eb;
        }

        .error-fallback--low {
          background-color: #d4edda;
          border-color: #c3e6cb;
        }

        .error-content {
          max-width: 500px;
          text-align: center;
        }

        .error-header {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .error-icon {
          font-size: 2rem;
          margin-right: 0.5rem;
        }

        .error-title {
          margin: 0;
          color: #495057;
        }

        .error-message {
          margin-bottom: 1.5rem;
          color: #6c757d;
        }

        .error-suggestions {
          text-align: left;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background-color: rgba(255, 255, 255, 0.5);
          border-radius: 4px;
        }

        .error-suggestions h4 {
          margin-top: 0;
          margin-bottom: 0.5rem;
          color: #495057;
        }

        .error-suggestions ul {
          margin: 0;
          padding-left: 1.5rem;
        }

        .error-suggestions li {
          margin-bottom: 0.25rem;
          color: #6c757d;
        }

        .error-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.15s ease-in-out;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #545b62;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .error-technical {
          text-align: left;
          margin-top: 1rem;
        }

        .error-technical summary {
          cursor: pointer;
          margin-bottom: 0.5rem;
          color: #6c757d;
        }

        .error-details {
          font-size: 0.75rem;
          color: #6c757d;
          background-color: #f8f9fa;
          padding: 0.75rem;
          border-radius: 4px;
          border: 1px solid #dee2e6;
        }

        .error-details p {
          margin: 0.25rem 0;
        }

        .error-details pre {
          margin: 0.5rem 0 0 0;
          padding: 0.5rem;
          background-color: #fff;
          border: 1px solid #dee2e6;
          border-radius: 2px;
          overflow-x: auto;
          font-size: 0.6875rem;
        }
      `}</style>
    </div>
  );
};

// ====================================
// Specialized Error Boundaries
// ====================================

/**
 * API Error Boundary - specialized for API-related errors
 */
export const ApiErrorBoundary: React.FC<
  Omit<ErrorBoundaryProps, 'enableAutoRetry'>
> = (props) => (
  <ErrorBoundary {...props} enableAutoRetry={true} maxRetries={3} />
);

/**
 * UI Error Boundary - specialized for UI component errors
 */
export const UIErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => (
  <ErrorBoundary {...props} enableAutoRetry={false} isolate={true} />
);

/**
 * Route Error Boundary - specialized for route-level errors
 */
export const RouteErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => (
  <ErrorBoundary {...props} enableAutoRetry={false} maxRetries={1} />
);

export default ErrorBoundary;
